// Performance monitoring utility to help diagnose animation issues

interface PerformanceMetrics {
  frameRate: number;
  droppedFrames: number;
  averageFrameTime: number;
  maxFrameTime: number;
  minFrameTime: number;
}

class PerformanceMonitor {
  private frameCount = 0;
  private lastTime = performance.now();
  private frameTimes: number[] = [];
  private isMonitoring = false;
  private rafId: number | null = null;
  private startTime: number = 0;
  private maxSampleSize = 300; // ~5 seconds at 60fps

  startMonitoring() {
    if (this.isMonitoring) return;
    
    this.isMonitoring = true;
    this.frameCount = 0;
    this.lastTime = performance.now();
    this.startTime = performance.now();
    this.frameTimes = [];
    
    console.log('📊 Starting performance monitoring...');
    this.monitor();
  }

  stopMonitoring() {
    if (!this.isMonitoring) return;
    
    this.isMonitoring = false;
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    
    const metrics = this.getMetrics();
    this.logMetrics(metrics);
  }

  private monitor() {
    if (!this.isMonitoring) return;

    const currentTime = performance.now();
    const frameTime = currentTime - this.lastTime;
    
    // Track frame times
    this.frameTimes.push(frameTime);
    if (this.frameTimes.length > this.maxSampleSize) {
      this.frameTimes.shift();
    }
    
    // Detect dropped frames (frame time > 20ms at 60fps means dropped frame)
    // Only log significant drops (>30ms) to avoid console spam
    if (frameTime > 30) {
      const droppedFrames = Math.floor(frameTime / 16.67) - 1;
      if (droppedFrames > 0) {
        console.warn(`⚠️ Significant frame drop: ${droppedFrames} frame(s). Frame time: ${frameTime.toFixed(2)}ms`);
      }
    }
    
    this.frameCount++;
    this.lastTime = currentTime;
    
    this.rafId = requestAnimationFrame(() => this.monitor());
  }

  getMetrics(): PerformanceMetrics {
    if (this.frameTimes.length === 0) {
      return {
        frameRate: 0,
        droppedFrames: 0,
        averageFrameTime: 0,
        maxFrameTime: 0,
        minFrameTime: 0
      };
    }

    const totalTime = this.frameTimes.reduce((a, b) => a + b, 0);
    const averageFrameTime = totalTime / this.frameTimes.length;
    const frameRate = 1000 / averageFrameTime;
    const maxFrameTime = Math.max(...this.frameTimes);
    const minFrameTime = Math.min(...this.frameTimes);
    
    // Count dropped frames (frame time > 20ms)
    const droppedFrames = this.frameTimes.filter(ft => ft > 20).length;

    return {
      frameRate,
      droppedFrames,
      averageFrameTime,
      maxFrameTime,
      minFrameTime
    };
  }

  logMetrics(metrics: PerformanceMetrics) {
    const duration = (performance.now() - this.startTime) / 1000;
    console.log('📊 Performance Metrics:');
    console.log(`  Duration: ${duration.toFixed(2)}s`);
    console.log(`  Frames: ${this.frameCount}`);
    console.log(`  Average FPS: ${metrics.frameRate.toFixed(2)}`);
    console.log(`  Average Frame Time: ${metrics.averageFrameTime.toFixed(2)}ms`);
    console.log(`  Min Frame Time: ${metrics.minFrameTime.toFixed(2)}ms`);
    console.log(`  Max Frame Time: ${metrics.maxFrameTime.toFixed(2)}ms`);
    console.log(`  Dropped Frames: ${metrics.droppedFrames} (${((metrics.droppedFrames / this.frameCount) * 100).toFixed(2)}%)`);
    
    if (metrics.frameRate < 55) {
      console.warn('⚠️ Low frame rate detected! This may cause stuttering.');
    }
    if (metrics.droppedFrames > this.frameCount * 0.1) {
      console.warn('⚠️ High dropped frame rate! More than 10% of frames are being dropped.');
    }
  }

  getCurrentFPS(): number {
    if (this.frameTimes.length === 0) return 0;
    const recent = this.frameTimes.slice(-60); // Last 60 frames
    const avg = recent.reduce((a, b) => a + b, 0) / recent.length;
    return 1000 / avg;
  }
}

export const performanceMonitor = new PerformanceMonitor();

// Auto-start monitoring when module loads (for development)
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  // Uncomment to auto-start monitoring
  // performanceMonitor.startMonitoring();
  
  // Log FPS every 5 seconds
  // setInterval(() => {
  //   const fps = performanceMonitor.getCurrentFPS();
  //   if (fps > 0) {
  //     console.log(`Current FPS: ${fps.toFixed(2)}`);
  //   }
  // }, 5000);
}

